import type {CSSProperties, ReactNode} from 'react';
import {cn} from '@chekinapp/ui';
import {DOCUMENT_LAYOUTS} from './layouts';
import type {DocumentVisual, LayoutZone} from './types';
import {getFallbackAspectRatio, useDocumentVisualImage} from './useDocumentVisualImage';
import styles from './documentCameraOutline.module.css';

type Props = {
  visual: DocumentVisual;
};

const PASSPORT_CODE_LINES = [
  'P<XXXMOCK<<DEMO<<<<<<<<<<<<<<<<<<<<<<<<<<<<<',
  'DEMO000012XXX9001018M3101018<<<<<<<<<<<<<<<<',
];
const CARD_CODE_LINES = [
  'IDXXXDEMO00012345<<<<<<<<<<<<<',
  '9001017M3101018XXX<<<<<<<<<<<2',
  'MOCK<<DEMO<<<<<<<<<<<<<<<<<<<<',
];

const QR_MODULES = [
  [9, 0, 2, 2],
  [12, 1, 1, 2],
  [9, 3, 1, 3],
  [11, 5, 2, 1],
  [8, 8, 2, 2],
  [11, 9, 1, 2],
  [13, 8, 2, 1],
  [16, 9, 2, 2],
  [0, 9, 2, 1],
  [3, 9, 1, 2],
  [5, 11, 2, 1],
  [8, 12, 1, 2],
  [10, 13, 2, 1],
  [14, 12, 2, 1],
  [12, 15, 1, 3],
  [15, 16, 2, 2],
  [9, 18, 2, 1],
  [18, 13, 2, 2],
];

const zoneStyle = (
  [, x, y, width, height]: LayoutZone,
  index: number,
  extra?: Record<string, string | number>,
) =>
  ({
    left: `${x}%`,
    top: `${y}%`,
    width: `${width}%`,
    height: `${height}%`,
    '--i': index,
    ...extra,
  } as CSSProperties);

function QrIcon() {
  return (
    <svg
      viewBox="0 0 21 21"
      preserveAspectRatio="xMidYMid meet"
      shapeRendering="crispEdges"
    >
      <g fill="none" stroke="currentColor" strokeWidth="1">
        <rect x=".5" y=".5" width="6" height="6" />
        <rect x="14.5" y=".5" width="6" height="6" />
        <rect x=".5" y="14.5" width="6" height="6" />
      </g>
      <g fill="currentColor">
        <rect x="2" y="2" width="3" height="3" />
        <rect x="16" y="2" width="3" height="3" />
        <rect x="2" y="16" width="3" height="3" />
        {QR_MODULES.map(([x, y, width, height]) => (
          <rect key={`${x}-${y}`} x={x} y={y} width={width} height={height} />
        ))}
      </g>
    </svg>
  );
}

function renderZone(zone: LayoutZone, index: number, isPassport: boolean): ReactNode {
  const [type, , , width, height, lines] = zone;

  switch (type) {
    case 'photo':
    case 'ghost':
      return (
        <i
          key={index}
          className={cn(styles.part, styles.photo, type === 'ghost' && styles.ghost)}
          style={zoneStyle(zone, index)}
        >
          <b />
        </i>
      );
    case 'chip':
      return (
        <i
          key={index}
          className={cn(styles.part, styles.chip)}
          style={zoneStyle(zone, index)}
        />
      );
    case 'qr':
      return (
        <i
          key={index}
          className={cn(styles.part, styles.qr)}
          style={zoneStyle(zone, index)}
        >
          <QrIcon />
        </i>
      );
    case 'barcode':
      return (
        <i
          key={index}
          className={cn(styles.part, styles.barcode)}
          style={zoneStyle(zone, index)}
        />
      );
    case 'text': {
      const count = Math.max(1, Math.min(lines ?? 2, 8));
      return (
        <i
          key={index}
          className={cn(styles.part, styles.text)}
          style={zoneStyle(zone, index, {'--lines': count})}
        >
          {Array.from({length: count}, (_, line) => (
            <s key={line} />
          ))}
        </i>
      );
    }
    case 'mrz': {
      const source = isPassport ? PASSPORT_CODE_LINES : CARD_CODE_LINES;
      const count = Math.max(1, Math.min(lines ?? source.length, 3));
      const characters = isPassport ? 44 : 30;
      const fontSize = `min(${((height / count) * 0.72).toFixed(3)}cqh, ${(
        width /
        (characters * 0.62)
      ).toFixed(3)}cqw)`;
      return (
        <i
          key={index}
          className={cn(styles.part, styles.code)}
          style={zoneStyle(zone, index, {fontSize})}
        >
          {Array.from({length: count}, (_, line) => (
            <span key={line}>
              {[...source[Math.min(line, source.length - 1)].slice(0, characters)].map(
                (character, position) => (
                  <s key={position}>{character}</s>
                ),
              )}
            </span>
          ))}
        </i>
      );
    }
    default:
      return null;
  }
}

function getGenericLayout(visual: DocumentVisual): LayoutZone[] {
  if (visual.kind === 'passport') return DOCUMENT_LAYOUTS['generic-passport'] ?? [];
  const image = visual.side === 'front' ? 'generic-id-front' : 'generic-id-back';
  return (DOCUMENT_LAYOUTS[image] ?? []).filter(([type]) => type !== 'mrz');
}

function getOutlineZones(visual: DocumentVisual): LayoutZone[] {
  const layout = DOCUMENT_LAYOUTS[visual.image] ?? getGenericLayout(visual);
  const [, , width, height] = visual.mrz;
  const hasCatalogCode = width > 0 && height > 0;
  if (layout.some(([type]) => type === 'mrz') || !hasCatalogCode) return layout;
  return [...layout, ['mrz', ...visual.mrz]];
}

function DocumentCameraOutline({visual}: Props) {
  const {aspectRatio} = useDocumentVisualImage(visual.image);
  const isPassport = visual.kind === 'passport';
  const ratio = aspectRatio ?? getFallbackAspectRatio(visual.kind);
  const zones = getOutlineZones(visual);

  return (
    <div className={styles.fit} aria-hidden>
      <div className={styles.outline} style={{'--ratio': ratio} as CSSProperties}>
        {zones.map((zone, index) => renderZone(zone, index, isPassport))}
        <i className={styles.scan} />
      </div>
    </div>
  );
}

export {DocumentCameraOutline};
